// Deeplinks in die Vario-Weboberfläche. Das Modul läuft als iframe IN der Tenant-Vario-Instanz
// (`https://<tenant>.vario.cloud`), daher leiten wir den Host aus dem Embedder ab statt ihn zu
// konfigurieren/hardcoden. Beleg-Deeplink: `<host>/documents-sales/<documentId>`.

/**
 * Tenant-Vario-Host (Origin) aus dem iframe-Embedder. `ancestorOrigins` (Chromium/Safari) zuerst,
 * dann `document.referrer` (Firefox/Fallback); beides gegen `*.vario.cloud` / `*.vario.de` validiert.
 * Gibt `null` zurück, wenn nicht ermittelbar (z. B. Standalone) → Aufrufer rendert dann keinen Link.
 */
export function varioBaseOrigin(): string | null {
  const isVarioHost = (host: string) => /(^|\.)vario\.(cloud|de)$/i.test(host);
  try {
    const ao = window.location.ancestorOrigins;
    if (ao && ao.length > 0) {
      const u = new URL(ao[0]!); // unmittelbarer Embedder = Vario-Host
      if (isVarioHost(u.hostname)) return u.origin;
    }
  } catch {
    /* ancestorOrigins nicht überall verfügbar */
  }
  try {
    if (document.referrer) {
      const u = new URL(document.referrer);
      if (isVarioHost(u.hostname)) return u.origin;
    }
  } catch {
    /* referrer leer/ungültig */
  }
  return null;
}

/**
 * Direkte Anzeige-Adresse eines freigegebenen DMS-Bildes.
 *
 * Der Worker baut `article_allowed_images.public_url` aus dem Vario-API-Host, und der ist bei
 * Mandanten ohne benannte Subdomain der NUMERISCHE Realm-Host (`tn0000002012.vario.cloud`). Auf dem
 * antwortet der öffentliche DMS-Endpunkt mit 500 „can't determine tenant from url" — deshalb galt
 * `public_url` bisher als unbrauchbar und jede Vorschau lief über `vario-dms-image-proxy`.
 *
 * Geprobt am 27.08.2026 mit DERSELBEN Adresse, nur anderem Host:
 *   `tn0000002012.vario.cloud/api/vario/public/dms/<datei>.png` → 500
 *   `pfannenshop.vario.cloud/api/vario/public/dms/<datei>.png`  → 200 image/png
 *
 * Der benannte Host steht im Browser ohnehin zur Verfügung: wir laufen als iframe IN der
 * Tenant-Instanz. Also Pfad aus `public_url` übernehmen und auf den Embedder-Origin setzen.
 *
 * `null`, wenn keine Freigabe existiert, der Pfad nicht die öffentliche DMS-Route ist, die
 * Dateiendung fehlt (ohne Endung antwortet Vario 404) oder der Host nicht ermittelbar ist. Der
 * Aufrufer fällt dann auf den Proxy zurück.
 */
export function varioPublicDmsUrl(publicUrl: string | null | undefined): string | null {
  if (!publicUrl) return null;
  const base = varioBaseOrigin();
  if (!base) return null;
  let pfad: string;
  try {
    pfad = new URL(publicUrl).pathname;
  } catch {
    return null;
  }
  if (!pfad.startsWith('/api/vario/public/dms/')) return null;
  // Ohne Dateiendung liefert der öffentliche Endpunkt 404 (Vario hängt sie erst an, wenn die Datei
  // verarbeitet ist; siehe `gambio-ensure-dms-share`). Dann lohnt der Versuch nicht.
  if (!/\.[A-Za-z0-9]{2,5}$/.test(pfad)) return null;
  return `${base}${pfad}`;
}

/**
 * Deeplink zum Vario-Verkaufsbeleg über die Dokument-ID. `null`, wenn ID fehlt oder der Tenant-Host
 * nicht ermittelbar ist → der Aufrufer zeigt dann die Nummer ohne Link.
 */
export function varioDocumentUrl(documentId: string | null | undefined): string | null {
  if (!documentId) return null;
  const base = varioBaseOrigin();
  return base ? `${base}/documents-sales/${encodeURIComponent(documentId)}` : null;
}

/**
 * Deeplink zur Vario-Artikel-Detailseite über die interne Artikel-ID. `null`, wenn ID fehlt oder der
 * Tenant-Host nicht ermittelbar ist → der Aufrufer zeigt dann die Artikelnummer ohne Link.
 */
export function varioArticleUrl(articleId: string | null | undefined): string | null {
  if (!articleId) return null;
  const base = varioBaseOrigin();
  return base ? `${base}/products/articles/${encodeURIComponent(articleId)}/detail` : null;
}

/**
 * Deeplink zum Vario-VERKAUFSKANAL (Kanal-Listing eines Artikels):
 *   `<host>/products/articles/{articleId}/listings/{listingId}`
 * Braucht Artikel-ID UND Vario-Listing-ID. Fehlt eine ID oder der Tenant-Host nicht ermittelbar
 * ist, gibt null zurueck. Der Aufrufer faellt dann auf varioArticleUrl (Artikel-Detail) zurueck.
 */
export function varioListingUrl(articleId: string | null | undefined, listingId: string | null | undefined): string | null {
  if (!articleId || !listingId) return null;
  const base = varioBaseOrigin();
  return base ? `${base}/products/articles/${encodeURIComponent(articleId)}/listings/${encodeURIComponent(listingId)}` : null;
}

/**
 * Deeplink zur Vario-Variantenverwaltung (Attribute + Werte + Schemata):
 *   `<host>/settings/products/productVariants#productVariantAttributes`
 * Öffnet die Verwaltung direkt auf dem Attribut-Tab. `null`, wenn der Tenant-Host nicht
 * ermittelbar ist (z. B. Standalone) → der Aufrufer zeigt dann keinen Link.
 */
export function varioVariantAdminUrl(): string | null {
  const base = varioBaseOrigin();
  return base ? `${base}/settings/products/productVariants#productVariantAttributes` : null;
}

/**
 * Deeplink zur Vario-Zahlungsartenverwaltung: `<host>/settings/finances/paymentModalities`.
 * Dort werden Zahlungsart und Zahlungsbedingung gepflegt, die in der Zuordnung links stehen.
 * `null`, wenn der Tenant-Host nicht ermittelbar ist (z. B. Standalone) → kein Link.
 */
export function varioPaymentModalitiesUrl(): string | null {
  const base = varioBaseOrigin();
  return base ? `${base}/settings/finances/paymentModalities` : null;
}

/**
 * Deeplink zur Vario-Versenderverwaltung: `<host>/settings/stocks/carriers`.
 * Dort werden die Versanddienstleister gepflegt, die in der Paketdienst-Zuordnung links stehen.
 * `null`, wenn der Tenant-Host nicht ermittelbar ist → kein Link.
 */
export function varioCarrierAdminUrl(): string | null {
  const base = varioBaseOrigin();
  return base ? `${base}/settings/stocks/carriers` : null;
}

/**
 * Deeplink zur Vario-Steuerverwaltung: `<host>/settings/finances/taxes`.
 * Dort werden die Steuersätze und -schemata gepflegt, die in der Steuerklassen-Zuordnung links
 * stehen. Route vom Betreiber genannt (2026-08-25), nichts geraten.
 * `null`, wenn der Tenant-Host nicht ermittelbar ist → kein Link.
 */
export function varioTaxesAdminUrl(): string | null {
  const base = varioBaseOrigin();
  return base ? `${base}/settings/finances/taxes` : null;
}

/**
 * Deeplink zur Vario-Versandartenverwaltung: `<host>/settings/stocks/deliveryModalities`.
 * Dort werden Versandart und Versandbedingung gepflegt, die in der Zuordnung links stehen.
 * `null`, wenn der Tenant-Host nicht ermittelbar ist → kein Link.
 */
export function varioDeliveryModalitiesUrl(): string | null {
  const base = varioBaseOrigin();
  return base ? `${base}/settings/stocks/deliveryModalities` : null;
}

/**
 * Deeplink zur Vario-Zaehlerkreisverwaltung: `<host>/settings/general/counters`.
 * Dort steht die Zaehlerkonfiguration "Kundennummer" mit Vergabeverfahren, Laenge und
 * Auffuellzeichen. Route vom Betreiber genannt (2026-08-26), nichts geraten.
 * `null`, wenn der Tenant-Host nicht ermittelbar ist -> kein Link.
 */
export function varioCountersUrl(): string | null {
  const base = varioBaseOrigin();
  return base ? `${base}/settings/general/counters` : null;
}

/**
 * Deeplink zur Vario-Kontakttypenverwaltung: `<host>/settings/accounts/contactType`.
 * Dort werden die Kontakt-Label gepflegt, die die GPSR-Rollenzuordnung aufloest.
 * `null`, wenn der Tenant-Host nicht ermittelbar ist -> kein Link.
 */
export function varioContactTypesUrl(): string | null {
  const base = varioBaseOrigin();
  return base ? `${base}/settings/accounts/contactType` : null;
}

/**
 * Deeplink zur Vario-Inventurverwaltung: `<host>/storages/inventories`.
 * Dort landen die Zaehllisten, die die App anlegt — Pruefen, Einkaufspreise nachtragen und
 * Einbuchen passiert ausschliesslich dort. Route vom Betreiber genannt (2026-08-28), nichts
 * geraten. `null`, wenn der Tenant-Host nicht ermittelbar ist -> kein Link.
 */
export function varioInventoriesUrl(): string | null {
  const base = varioBaseOrigin();
  return base ? `${base}/storages/inventories` : null;
}

/**
 * Deeplink zur Vario-Waehrungsverwaltung: `<host>/settings/finances/currencies`.
 * `null`, wenn der Tenant-Host nicht ermittelbar ist -> kein Link.
 */
export function varioCurrenciesUrl(): string | null {
  const base = varioBaseOrigin();
  return base ? `${base}/settings/finances/currencies` : null;
}
